<script setup lang="ts">
  import {
    OrganisationsService,
    EmailsService,
    type CustomerOrganisation,
    UsersService,
    type CustomerUser as User,
    type EmailRenderTemplateResponse
  } from '@/types/generated'
  import { onMounted, ref, computed, watch } from 'vue'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import {
    EDITOR_COLORS,
    EDITOR_TEXT_SIZES,
    EDITOR_FONT_FAMILIES
  } from '@/shared/constants/app/editor/editor'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import EmailPreview from '@/components/app/email/EmailPreview.vue'
  import { GENERIC_EMAIL } from '@/constants/settings/email'
  import { useTranslation } from '@/composables/useTranslation'
  import { TriangleAlertIcon } from 'lucide-vue-next'

  const org = ref<CustomerOrganisation>()
  const user = ref<User>()
  const rawEmailTemplateHTML = computed(() => {
    return GENERIC_EMAIL + user.value?.email_signature + org.value?.email_signature
  })
  const template = ref<EmailRenderTemplateResponse>()

  const textFontOptions = computed(() => {
    return Object.entries(EDITOR_FONT_FAMILIES).map(([key, title]) => ({
      value: key,
      title: title
    }))
  })

  const textColorOptions = computed(() => {
    return Object.values(EDITOR_COLORS).map((value) => {
      return {
        value: value,
        title: value
      }
    })
  })

  const textSizeOptions = computed(() => {
    return Object.values(EDITOR_TEXT_SIZES).map((value) => {
      return {
        value: value,
        title: `${value}`
      }
    })
  })

  onMounted(async () => {
    org.value = (await OrganisationsService.organisationsRetrieve({
      uuid: 'current'
    })) as CustomerOrganisation
    user.value = (await UsersService.usersRetrieve({
      uuid: 'current'
    })) as User
    template.value = await EmailsService.emailsRenderTemplateCreate({
      requestBody: {
        template_html: rawEmailTemplateHTML.value,
        subject: `Ihre Anfrage bei ${org.value.name}`
      }
    })
  })
  watch(
    org,
    async () => {
      await OrganisationsService.organisationsPartialUpdate({
        uuid: org.value.uuid,
        requestBody: {
          email_text_font: org.value.email_text_font,
          email_text_color: org.value.email_text_color,
          email_text_size: org.value.email_text_size
        }
      })
      template.value = await EmailsService.emailsRenderTemplateCreate({
        requestBody: {
          template_html: rawEmailTemplateHTML.value,
          subject: `Ihre Anfrage bei ${org.value.name}`
        }
      })
    },
    { deep: true }
  )

  const { t } = useTranslation({
    en: {
      hint: 'These styles will be applied to the email content of all users in your organization. However, they will not override any custom styles that individual users have set in their email signatures.',
      fontFamily: 'Font family',
      fontColor: 'Font color',
      fontSize: 'Font size',
      preview: 'Preview'
    },
    de: {
      hint: 'Diese Stile werden auf den E-Mail-Inhalt aller Benutzer in Ihrer Organisation angewendet. Sie setzen jedoch keine benutzerdefinierten Stile außer Kraft, die einzelne Benutzer in ihren E-Mail-Signaturen festgelegt haben.',
      fontFamily: 'Schriftart',
      fontColor: 'Schriftfarbe',
      fontSize: 'Schriftgröße',
      preview: 'Vorschau'
    }
  })
</script>
<template>
  <SettingsPage>
    <div v-if="org && user">
      <div class="mb-6 flex gap-4">
        <!-- 60% width of the parent -->
        <!-- Schriftart -->
        <div class="w-32">
          <div class="w-full">
            <!-- Adjust width as needed, e.g., w-32 for roughly 32 units of the available width -->
            <SelectField
              v-model="org.email_text_font"
              :label="t('fontFamily')"
              :options="textFontOptions"
              :is-filterable="false"
              name="email_text_font"
            >
              <template #button-contents="{ selectedOption }">
                <span
                  class="truncate text-gray-800"
                  :style="`font-family: ${selectedOption.value}`"
                >
                  {{ selectedOption === null ? selectedOption : selectedOption.title }}
                </span>
              </template>
              <template #option-contents="{ option }">
                <span
                  class="text-gray-800"
                  :style="`font-family: ${option.value}`"
                >
                  {{ option.title }}
                </span>
              </template>
            </SelectField>
          </div>
        </div>

        <!-- Schriftfarbe -->
        <div class="">
          <!-- Adjust width -->
          <SelectField
            v-model="org.email_text_color"
            :label="t('fontColor')"
            :options="textColorOptions"
            :is-filterable="false"
            name="email_text_color"
          >
            <template #button-contents="{ selectedOption }">
              <div
                class="me-2 inline-block h-4 w-4"
                :style="`background-color: ${selectedOption.value};`"
              ></div>
              <span class="truncate text-gray-800">
                {{ selectedOption === null ? selectedOption : selectedOption.title }}
              </span>
            </template>
            <template #option-contents="{ option }">
              <div
                class="me-2 inline-block h-4 w-4"
                :style="`background-color: ${option.value};`"
              ></div>
              <span class="truncate text-gray-800">
                {{ option.title }}
              </span>
            </template>
          </SelectField>
        </div>

        <!-- Schriftgröße -->
        <div class="w-32">
          <div class="w-full">
            <!-- Adjust width -->
            <SelectField
              v-model="org.email_text_size"
              :label="t('fontSize')"
              :options="textSizeOptions"
              name="email_text_size"
            />
          </div>
        </div>
      </div>
      <div class="mb-3 w-full overflow-hidden">
        <div class="max-w-content flex w-full items-center gap-3">
          <TriangleAlertIcon class="inline-block h-4 w-4 text-gray-400" />
          <p class="w-full text-wrap text-gray-400">
            {{ t('hint') }}
          </p>
        </div>
      </div>
      <div class="mb-3 block font-medium">{{ t('preview') }}</div>
      <EmailPreview
        v-if="template"
        :subject="template.subject"
        recipient-email="max.mustermann@outlook.com"
        :from-email="org.email"
        :text-html="template.html"
      />
    </div>
  </SettingsPage>
</template>
